import React, { useState, useCallback, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { Button, Typography, Box, Dialog, DialogTitle, DialogContent, DialogActions, TextField, List, ListItem, ListItemText, ListItemSecondaryAction, IconButton, CircularProgress, Chip, Paper, LinearProgress, Modal } from '@material-ui/core';
import { Search, FileCopy, CheckCircle, Error, CloudUpload } from '@material-ui/icons';
import swal from 'sweetalert';
import firebase from "firebase/app";
import TopMenu from '../TopMenu';
import ExamMetaForm from './ExamMetaForm';
import SectionEditor from './SectionEditor';
import ExamPreview from './ExamPreview';
import EnhancedExamSearch from './EnhancedExamSearch';
import AdvancedImageOptimizer from '../../services/AdvancedImageOptimizer';
import ShortUrlService from '../../services/ShortUrlService';
import AuthenticatedUrlCache from '../../services/AuthenticatedUrlCache';
import ExamCacheService from '../../services/ExamCacheService';

// Utility to convert a timestamp to IST-local datetime-local input string
function toISTLocalInputString(timestamp) {
  if (!timestamp) return '';
  const date = new Date(typeof timestamp === 'number'
    ? (timestamp < 1e12 ? timestamp * 1000 : timestamp)
    : Date.parse(timestamp));
  // Convert to IST (UTC+5:30)
  const offsetMs = 5.5 * 60 * 60 * 1000;
  const istDate = new Date(date.getTime() + offsetMs);
  const pad = n => n.toString().padStart(2, '0');
  return `${istDate.getFullYear()}-${pad(istDate.getMonth() + 1)}-${pad(istDate.getDate())}T${pad(istDate.getHours())}:${pad(istDate.getMinutes())}`;
}

// Utility to parse IST-local input string to UTC timestamp
function parseISTInputToUTCTimestamp(input) {
  if (!input) return null;
  // input is in 'YYYY-MM-DDTHH:mm' and is IST
  const [datePart, timePart] = input.split('T');
  const [year, month, day] = datePart.split('-').map(Number);
  const [hour, minute] = timePart.split(':').map(Number);
  // Create a Date object in IST
  const istDate = new Date(Date.UTC(year, month - 1, day, hour - 5, minute - 30));
  // Add IST offset to get UTC timestamp
  return istDate.getTime();
}

// Debounce utility function
const debounce = (func, wait) => {
  let timeout;
  return function executedFunction(...args) {
    const later = () => {
      clearTimeout(timeout);
      func(...args);
    };
    clearTimeout(timeout);
    timeout = setTimeout(later, wait);
  };
};

// Function to get display URL for images (handles both legacy and new)
const getDisplayUrl = async (imageUrl) => {
  if (!imageUrl) return '';
  
  // Handle blob URLs (temporary URLs for cloned images) - return as-is
  if (imageUrl.startsWith('blob:')) {
    return imageUrl;
  }
  
  // Handle data URLs (base64 images) - return as-is
  if (imageUrl.startsWith('data:image')) {
    return imageUrl;
  }
  
  let finalUrl;
  
  // If it's a short URL (starts with q- or img_), convert to full URL
  if (imageUrl.startsWith('q-') || imageUrl.startsWith('img_')) {
    // Try to use ImageUrlMigrationService first, fallback to ShortUrlService
    try {
      const { default: ImageUrlMigrationService } = await import('../../services/ImageUrlMigrationService');
      finalUrl = await ImageUrlMigrationService.expandImageUrl(imageUrl);
    } catch (error) {
      // Fallback to ShortUrlService for legacy support
      finalUrl = await ShortUrlService.toFullUrl(imageUrl);
    }
  } else {
    // Otherwise, return as-is (should be full Firebase Storage URL)
    finalUrl = imageUrl;
  }
  
  // Handle Firebase Storage URLs with authentication
  if (finalUrl.includes('firebasestorage.googleapis.com')) {
    // Always use authenticated access for secure storage
    try {
      const authenticatedUrl = await AuthenticatedUrlCache.getAuthenticatedUrl(finalUrl);
      return authenticatedUrl;
    } catch (error) {
      console.error('Failed to get authenticated URL:', error);
      // Return the original URL as fallback instead of throwing error
      return finalUrl;
    }
  }
  
  return finalUrl;
};

// Authenticated Image Component
const AuthenticatedImage = ({ imageUrl, alt, style }) => {
  const [src, setSrc] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);
  

  useEffect(() => {
    const loadImage = async () => {
      try {
        setLoading(true);
        setError(false);
        const authenticatedUrl = await getDisplayUrl(imageUrl);
        setSrc(authenticatedUrl);
      } catch (err) {
        console.error('Failed to load authenticated image:', err);
        setError(true);
      } finally {
        setLoading(false);
      }
    };

    if (imageUrl) {
      loadImage();
    }
  }, [imageUrl]);

  if (loading) {
    return <div style={{ ...style, display: 'flex', alignItems: 'center', justifyContent: 'center', background: '#f5f5f5' }}>Loading image...</div>;
  }

  if (error) {
    return (
      <div style={{ 
        ...style, 
        display: 'flex', 
        alignItems: 'center', 
        justifyContent: 'center', 
        background: '#ffebee', 
        color: '#d32f2f',
        fontSize: '12px',
        textAlign: 'center',
        padding: '8px'
      }}>
        🔒 Authentication required<br/>
        Please login to view this image
      </div>
    );
  }

  return (
    <img 
      src={src} 
      alt={alt} 
      style={style}
      onError={(e) => {
        console.error('Image failed to load:', src);
        e.target.style.display = 'none';
      }}
    />
  );
};

const DynamicExamCreator = () => {
  const history = useHistory();
  const [examTitle, setExamTitle] = useState('');
  const [examCode, setExamCode] = useState('');
  const [examTimer, setExamTimer] = useState(60);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [sections, setSections] = useState([{ id: `s-${Date.now()}`, title: '', weightage: 100, questions: [] }]);
  const [showPreview, setShowPreview] = useState(false);
  const [validationErrors, setValidationErrors] = useState({});
  const [totalWeightage, setTotalWeightage] = useState(100);
  const [hasInteracted, setHasInteracted] = useState(false);
  const [fieldInteractions, setFieldInteractions] = useState(new Set());
  const [isSaveValidation, setIsSaveValidation] = useState(false);
  const [isCheckingDuplicate, setIsCheckingDuplicate] = useState(false);
  const [examCodeExists, setExamCodeExists] = useState(false);
  
  // Cheat Detection Settings
  const [cheatDetectionSettings, setCheatDetectionSettings] = useState({
    enableTabSwitchDetection: true,
    enableCopyPasteDetection: true,
    enableFullscreenDetection: true,
    enableWebcamDetection: true,
    enableKeyboardDetection: true,
    enableContextMenuDetection: true,
    enableSidePanelDetection: true,
    enableFocusLossDetection: true
  });

  // Clone exam functionality - Enhanced with optimized search
  const [selectedExam, setSelectedExam] = useState(null);
  const [isCloning, setIsCloning] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [migrationProgress, setMigrationProgress] = useState({ current: 0, total: 0 });
  const [showProgressModal, setShowProgressModal] = useState(false);
  const [progressStatus, setProgressStatus] = useState('uploading'); // 'uploading', 'success', 'error'
  
  // Separate storage for File objects to avoid JSON serialization issues
  const [pendingImageFiles, setPendingImageFiles] = useState(new Map());

  // Section and Question Handlers
  const handleSectionChange = useCallback((index, field, value) => {
    setIsSaveValidation(false); // Reset save validation flag when user makes changes
    setSections(curr => curr.map((sec, i) => i === index ? { ...sec, [field]: value } : sec));
  }, []);
  const addSection = useCallback(() => {
    setSections(curr => [...curr, { id: `s-${Date.now()}`, title: '', weightage: 0, questions: [] }]);
  }, []);
  const removeSection = useCallback((index) => {
    setSections(curr => curr.filter((_, i) => i !== index));
  }, []);
  const addQuestion = useCallback((sectionIndex) => {
    const newQuestion = {
      id: `q-${Date.now()}`,
      type: 'mcq-single',
      text: '',
      maxScore: 1, // Set default max score to 1
      problemDescription: '',
      instructions: '',
      boilerplateCode: '',
      options: [{ id: `o-${Date.now()}`, text: '', marks: 0, isCorrect: false }],
      enableAIEvaluation: false,
    };
    setSections(curr => curr.map((sec, i) => i === sectionIndex ? { ...sec, questions: [...sec.questions, newQuestion] } : sec));
  }, []);
  const removeQuestion = useCallback((sectionIndex, questionIndex) => {
    setSections(curr => {
      const newSections = curr.map((sec, i) => i === sectionIndex ? { ...sec, questions: sec.questions.filter((_, qi) => qi !== questionIndex) } : sec);
      return newSections;
    });
  }, []);
  const handleQuestionChange = useCallback((sectionIndex, questionIndex, field, value) => {
    // Handle File objects separately to avoid JSON serialization issues
    if (field === 'imageFile') {
    setSections(curr => {
        const questionId = curr[sectionIndex].questions[questionIndex].id;
        if (value) {
          // Store the File object in the separate Map
          setPendingImageFiles(prev => {
            const newMap = new Map(prev);
            newMap.set(questionId, value);
            return newMap;
          });
        } else {
          // Remove the File object from the Map
          setPendingImageFiles(prev => {
            const newMap = new Map(prev);
            newMap.delete(questionId);
            return newMap;
          });
        }
        
      const newSections = JSON.parse(JSON.stringify(curr));
      const question = newSections[sectionIndex].questions[questionIndex];
      question[field] = value;
      if (field === 'type') {
        const preservedText = question.text;
        question.problemDescription = '';
        question.instructions = '';
        question.boilerplateCode = '';
        question.evaluationKeywords = '';
        question.enableAIEvaluation = false;
        if (['mcq-single', 'mcq-multi'].includes(value)) {
          if (!question.options || question.options.length === 0) {
            question.options = [{ id: `o-${Date.now()}`, text: '', marks: 0, isCorrect: false }];
          }
        } else if (value === 'scenario-text') {
          if (!question.scenarioOptions || question.scenarioOptions.length === 0) {
            question.scenarioOptions = [{ title: '', evaluationPoints: [] }];
          }
        } else if (value === 'psyco') {
          // Clear options and scenario options for psyco questions
          question.options = [];
          question.scenarioOptions = [];
          question.psycoAnswer = '';
          question.problemDescription = '';
          question.instructions = '';
          question.boilerplateCode = '';
          question.evaluationKeywords = '';
          question.enableAIEvaluation = false;
        } else if (value === 'video') {
          // For video questions, clear scenario options and initialize evaluationPoints
          question.options = [];
          question.scenarioOptions = [];
          question.evaluationPoints = [];
        } else {
          question.options = [];
        }
        question.text = preservedText;
      }
      return newSections;
    });
    } else {
      setSections(curr => {
        const newSections = JSON.parse(JSON.stringify(curr));
        const question = newSections[sectionIndex].questions[questionIndex];
        question[field] = value;
        if (field === 'type') {
          const preservedText = question.text;
          question.problemDescription = '';
          question.instructions = '';
          question.boilerplateCode = '';
          question.evaluationKeywords = '';
          question.enableAIEvaluation = false;
          if (['mcq-single', 'mcq-multi'].includes(value)) {
            if (!question.options || question.options.length === 0) {
              question.options = [{ id: `o-${Date.now()}`, text: '', marks: 0, isCorrect: false }];
            }
          } else if (value === 'scenario-text') {
            if (!question.scenarioOptions || question.scenarioOptions.length === 0) {
              question.scenarioOptions = [{ title: '', evaluationPoints: [] }];
            }
          } else if (value === 'psyco') {
            // Clear options and scenario options for psyco questions
            question.options = [];
            question.scenarioOptions = [];
            question.psycoAnswer = '';
            question.problemDescription = '';
            question.instructions = '';
            question.boilerplateCode = '';
            question.evaluationKeywords = '';
            question.enableAIEvaluation = false;
          } else if (value === 'video') {
            // For video questions, clear scenario options and initialize evaluationPoints
            question.options = [];
            question.scenarioOptions = [];
            question.evaluationPoints = [];
          } else {
            question.options = [];
          }
          question.text = preservedText;
        }
        return newSections;
      });
    }
  }, []);

  // Check for duplicate exam code
  const checkExamCodeExists = useCallback(async (code) => {
    if (!code || code.trim().length < 3) {
      setExamCodeExists(false);
      return false;
    }
    
    setIsCheckingDuplicate(true);
    try {
      const snapshot = await firebase.database().ref(`TestPapers/${code.trim()}`).once('value');
      const exists = snapshot.exists();
      setExamCodeExists(exists);
      setIsCheckingDuplicate(false);
      return exists;
    } catch (error) {
      setIsCheckingDuplicate(false);
      return false;
    }
  }, []);

  // Validation functions
  const validateExamMeta = useCallback(() => {
    const errors = {};
    
    // CRITICAL: Exam code validation should ALWAYS be enforced to prevent database corruption
    if (!examCode.trim()) {
      errors.examCode = 'Exam code is required';
    } else if (examCode.trim().length < 3) {
      errors.examCode = 'Exam code must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9-_]+$/.test(examCode.trim())) {
      errors.examCode = 'Exam code can only contain letters, numbers, hyphens, and underscores';
    } else if (examCodeExists) {
      errors.examCode = 'This exam code already exists. Please choose a different code.';
    }
    
    // CRITICAL: Exam title validation should ALWAYS be enforced
    if (!examTitle.trim()) {
      errors.examTitle = 'Exam title is required';
    } else if (examTitle.trim().length < 3) {
      errors.examTitle = 'Exam title must be at least 3 characters';
    }
    
    // Other validations can be interaction-based
    if (!examTimer || examTimer < 1) {
      if (hasInteracted) {
        errors.examTimer = 'Exam timer must be at least 1 minute';
      }
    }
    
    if (!startDate) {
      if (hasInteracted) {
        errors.startDate = 'Start date is required';
      }
    }
    
    if (!endDate) {
      if (hasInteracted) {
        errors.endDate = 'End date is required';
      }
    }
    
    if (startDate && endDate) {
    const startDateTime = parseISTInputToUTCTimestamp(startDate);
    const endDateTime = parseISTInputToUTCTimestamp(endDate);
    if (startDateTime >= endDateTime) {
        errors.endDate = 'End date must be after start date';
      }
    }
    
    return errors;
  }, [examTitle, examCode, examTimer, startDate, endDate, hasInteracted, examCodeExists]);

  const validateSection = useCallback((section, sectionIndex) => {
    const errors = {};
    
    // Only validate section title if user has started typing or if it's the only section
    if (!section.title.trim()) {
      // Don't validate empty sections unless user has interacted
      if (hasInteracted) {
        errors[`section_${sectionIndex}_title`] = 'Section title is required';
      }
    }
    
    const weightage = Number(section.weightage);
    if (isNaN(weightage) || weightage < 0) {
      if (hasInteracted) {
        errors[`section_${sectionIndex}_weightage`] = 'Weightage must be a positive number';
      }
    } else if (weightage > 100) {
      if (hasInteracted) {
        errors[`section_${sectionIndex}_weightage`] = 'Weightage cannot exceed 100%';
      }
    }
    
    // Only validate empty questions if user has interacted
    if (section.questions.length === 0 && hasInteracted) {
      errors[`section_${sectionIndex}_questions`] = 'Section must have at least one question';
    }
    
    return errors;
  }, [hasInteracted]);

  const validateQuestion = useCallback((question, sectionIndex, questionIndex) => {
    const errors = {};
    
    // Only validate question text if user has interacted
    if (!question.text.trim() && hasInteracted) {
      errors[`question_${sectionIndex}_${questionIndex}_text`] = 'Question text is required';
    }
    
    if (question.maxScore === undefined || question.maxScore < 1) {
      if (hasInteracted) {
        errors[`question_${sectionIndex}_${questionIndex}_maxScore`] = 'Max score must be at least 1';
      }
    }
    
    // Validate MCQ options - only if user has interacted
    if (['mcq-single', 'mcq-multi'].includes(question.type)) {
      if (!question.options || question.options.length === 0) {
        if (hasInteracted) {
          errors[`question_${sectionIndex}_${questionIndex}_options`] = 'At least one option is required';
        }
      } else {
        question.options.forEach((option, optIndex) => {
          if (!option.text.trim() && hasInteracted) {
            errors[`question_${sectionIndex}_${questionIndex}_option_${optIndex}_text`] = 'Option text is required';
          }
          if (option.marks < 0) {
            if (hasInteracted) {
              errors[`question_${sectionIndex}_${questionIndex}_option_${optIndex}_marks`] = 'Option marks cannot be negative';
            }
          }
        });
        
        // Check if at least one option is correct for single-choice MCQ
        if (question.type === 'mcq-single' && !question.options.some(opt => opt.isCorrect) && hasInteracted) {
          errors[`question_${sectionIndex}_${questionIndex}_correct`] = 'At least one option must be marked as correct';
        }
      }
    }
    
    // Validate scenario-text options - only if user has interacted
    if (question.type === 'scenario-text') {
      if (!question.scenarioOptions || question.scenarioOptions.length === 0) {
        if (hasInteracted) {
          errors[`question_${sectionIndex}_${questionIndex}_scenarioOptions`] = 'At least one scenario option is required';
        }
      } else {
        question.scenarioOptions.forEach((option, optIndex) => {
          if (!option.title.trim() && hasInteracted) {
            errors[`question_${sectionIndex}_${questionIndex}_scenario_${optIndex}_title`] = 'Scenario option title is required';
          }
          if (!option.content || !option.content.trim()) {
            if (hasInteracted) {
              errors[`question_${sectionIndex}_${questionIndex}_scenario_${optIndex}_content`] = 'Scenario option content is required';
            }
          }
          if (!option.evaluationPoints || option.evaluationPoints.length === 0) {
            if (hasInteracted) {
              errors[`question_${sectionIndex}_${questionIndex}_scenario_${optIndex}_evaluationPoints`] = 'At least one evaluation point is required';
            }
          }
        });
      }
    }
    
    // Validate code questions - only if user has interacted
    if (question.type === 'code') {
      if (!question.problemDescription || !question.problemDescription.trim()) {
        if (hasInteracted) {
          errors[`question_${sectionIndex}_${questionIndex}_problemDescription`] = 'Problem description is required for coding questions';
        }
      }
    }
    
    // Validate psyco questions - only if user has interacted
    if (question.type === 'psyco') {
      if (!question.psycoAnswer || question.psycoAnswer.length !== 4) {
        if (hasInteracted) {
          errors[`question_${sectionIndex}_${questionIndex}_psycoAnswer`] = 'Psyco answer must be exactly 4 characters (A, B, C, D)';
        }
      } else if (!/^[ABCD]{4}$/.test(question.psycoAnswer)) {
        if (hasInteracted) {
          errors[`question_${sectionIndex}_${questionIndex}_psycoAnswer`] = 'Psyco answer must contain only A, B, C, D with no duplicates';
        }
      }
    }
    
    // Validate video questions - only if user has interacted
    if (question.type === 'video') {
      // For video recording questions, scenario options are completely optional
      // Candidates can record open-ended videos without selecting scenarios
      // Only validate scenario option text if scenarios are actually provided
      if (question.options && question.options.length > 0) {
        question.options.forEach((option, optIndex) => {
          if (!option.text.trim() && hasInteracted) {
            errors[`question_${sectionIndex}_${questionIndex}_option_${optIndex}_text`] = 'Scenario option text is required';
          }
        });
      }
      // No validation errors if no scenario options are provided - this is perfectly valid
    }
    
    return errors;
  }, [hasInteracted]);

  // Helper function to track field interactions
  const markFieldAsInteracted = useCallback((fieldKey) => {
    setFieldInteractions(prev => new Set([...prev, fieldKey]));
  }, []);

  const validateAll = useCallback(() => {
    const errors = {};
    
    // Validate exam meta
    Object.assign(errors, validateExamMeta());
    
    // Validate sections and questions
    sections.forEach((section, sectionIndex) => {
      Object.assign(errors, validateSection(section, sectionIndex));
      
      section.questions.forEach((question, questionIndex) => {
        Object.assign(errors, validateQuestion(question, sectionIndex, questionIndex));
      });
    });
    
    // Validate total weightage
    const totalWeight = sections.reduce((sum, section) => sum + (Number(section.weightage) || 0), 0);
    if (Math.abs(totalWeight - 100) > 0.1 && hasInteracted) {
      errors.totalWeightage = `Total section weightage must be 100%, but it is currently ${totalWeight}%`;
    }
    
    // Don't override save validation errors
    if (!isSaveValidation) {
      setValidationErrors(errors);
    }
    return Object.keys(errors).length === 0;
  }, [sections, validateExamMeta, validateSection, validateQuestion, isSaveValidation]);

  // CRITICAL: Create a strict validation function that validates everything without interaction checks
  const validateAllStrict = useCallback(() => {
    const errors = {};
    
    // CRITICAL: Always validate exam meta (exam code and title are critical)
    if (!examTitle.trim()) {
      errors.examTitle = 'Exam title is required';
    } else if (examTitle.trim().length < 3) {
      errors.examTitle = 'Exam title must be at least 3 characters';
    }
    
    if (!examCode.trim()) {
      errors.examCode = 'Exam code is required';
    } else if (examCode.trim().length < 3) {
      errors.examCode = 'Exam code must be at least 3 characters';
    } else if (!/^[a-zA-Z0-9-_]+$/.test(examCode.trim())) {
      errors.examCode = 'Exam code can only contain letters, numbers, hyphens, and underscores';
    } else if (examCodeExists) {
      errors.examCode = 'This exam code already exists. Please choose a different code.';
    }
    
    if (!examTimer || examTimer < 1) {
      errors.examTimer = 'Exam timer must be at least 1 minute';
    }
    
    if (!startDate) {
      errors.startDate = 'Start date is required';
    }
    
    if (!endDate) {
      errors.endDate = 'End date is required';
    }
    
    if (startDate && endDate) {
      const startDateTime = parseISTInputToUTCTimestamp(startDate);
      const endDateTime = parseISTInputToUTCTimestamp(endDate);
      if (startDateTime >= endDateTime) {
        errors.endDate = 'End date must be after start date';
      }
    }
    
    // CRITICAL: Always validate sections and questions
    sections.forEach((section, sectionIndex) => {
      if (!section.title.trim()) {
        errors[`section_${sectionIndex}_title`] = 'Section title is required';
      }
      
      const weightage = Number(section.weightage);
      if (isNaN(weightage) || weightage < 0) {
        errors[`section_${sectionIndex}_weightage`] = 'Weightage must be a positive number';
      } else if (weightage > 100) {
        errors[`section_${sectionIndex}_weightage`] = 'Weightage cannot exceed 100%';
      }
      
      if (section.questions.length === 0) {
        errors[`section_${sectionIndex}_questions`] = 'Section must have at least one question';
      }
      
      section.questions.forEach((question, questionIndex) => {
        if (!question.text.trim()) {
          errors[`question_${sectionIndex}_${questionIndex}_text`] = 'Question text is required';
        } else {
          // Only validate max score if question text is not empty
          const maxScore = Number(question.maxScore || 1); // Default to 1 if undefined
          if (isNaN(maxScore) || maxScore < 1) {
            errors[`question_${sectionIndex}_${questionIndex}_maxScore`] = 'Max score must be at least 1';
          }
        }
        
        // Validate MCQ options
        if (['mcq-single', 'mcq-multi'].includes(question.type)) {
          if (!question.options || question.options.length === 0) {
            errors[`question_${sectionIndex}_${questionIndex}_options`] = 'At least one option is required';
          } else {
            // Check if at least one option has marks greater than 0
            const hasValidMarks = question.options.some(option => Number(option.marks) > 0);
            
            question.options.forEach((option, optIndex) => {
              if (!option.text.trim()) {
                errors[`question_${sectionIndex}_${questionIndex}_option_${optIndex}_text`] = 'Option text is required';
              }
              const marks = Number(option.marks);
              if (isNaN(marks) || marks < 0) {
                errors[`question_${sectionIndex}_${questionIndex}_option_${optIndex}_marks`] = 'Option marks cannot be negative';
              }
            });
            
            // If no option has marks greater than 0, add a general error for the question
            if (!hasValidMarks) {
              const errorKey = `question_${sectionIndex}_${questionIndex}_marks`;
              errors[errorKey] = 'At least one option must have marks greater than 0';
            }
            
            if (question.type === 'mcq-single' && !question.options.some(opt => opt.isCorrect)) {
              errors[`question_${sectionIndex}_${questionIndex}_correct`] = 'At least one option must be marked as correct';
            }
          }
        }
        
        // Validate scenario-text options
        if (question.type === 'scenario-text') {
          if (!question.scenarioOptions || question.scenarioOptions.length === 0) {
            errors[`question_${sectionIndex}_${questionIndex}_scenarioOptions`] = 'At least one scenario option is required';
          } else {
            question.scenarioOptions.forEach((option, optIndex) => {
              if (!option.title.trim()) {
                errors[`question_${sectionIndex}_${questionIndex}_scenario_${optIndex}_title`] = 'Scenario option title is required';
              }
              if (!option.content || !option.content.trim()) {
                errors[`question_${sectionIndex}_${questionIndex}_scenario_${optIndex}_content`] = 'Scenario option content is required';
              }
              if (!option.evaluationPoints || option.evaluationPoints.length === 0) {
                errors[`question_${sectionIndex}_${questionIndex}_scenario_${optIndex}_evaluationPoints`] = 'At least one evaluation point is required';
              }
            });
          }
        }
        
        // Validate code questions
        if (question.type === 'code') {
          if (!question.problemDescription || !question.problemDescription.trim()) {
            errors[`question_${sectionIndex}_${questionIndex}_problemDescription`] = 'Problem description is required for coding questions';
          }
        }
        
        // Validate psyco questions
        if (question.type === 'psyco') {
          if (!question.psycoAnswer || question.psycoAnswer.length !== 4) {
            errors[`question_${sectionIndex}_${questionIndex}_psycoAnswer`] = 'Psyco answer must be exactly 4 characters (A, B, C, D)';
          } else if (!/^[ABCD]{4}$/.test(question.psycoAnswer)) {
            errors[`question_${sectionIndex}_${questionIndex}_psycoAnswer`] = 'Psyco answer must contain only A, B, C, D with no duplicates';
          }
        }
        
        // Validate video questions
        if (question.type === 'video') {
          // For video recording questions, scenario options are completely optional
          // Candidates can record open-ended videos without selecting scenarios
          // Only validate scenario option text if scenarios are actually provided
          if (question.options && question.options.length > 0) {
            question.options.forEach((option, optIndex) => {
              if (!option.text.trim()) {
                errors[`question_${sectionIndex}_${questionIndex}_option_${optIndex}_text`] = 'Scenario option text is required';
              }
            });
          }
          // No validation errors if no scenario options are provided - this is perfectly valid
        }
      });
    });
    
    // CRITICAL: Always validate total weightage
    const totalWeight = sections.reduce((sum, section) => sum + (Number(section.weightage) || 0), 0);
    if (Math.abs(totalWeight - 100) > 0.1) {
      errors.totalWeightage = `Total section weightage must be 100%, but it is currently ${totalWeight}%`;
    }
    
    return errors;
  }, [examTitle, examCode, examTimer, startDate, endDate, sections]);



  // Update total weightage when sections change
  useEffect(() => {
    const total = sections.reduce((sum, section) => sum + (Number(section.weightage) || 0), 0);
    setTotalWeightage(total);
  }, [sections]);

  // Check for duplicate exam code when exam code changes
  useEffect(() => {
    if (examCode.trim().length >= 3 && /^[a-zA-Z0-9-_]+$/.test(examCode.trim())) {
      checkExamCodeExists(examCode);
    } else {
      setExamCodeExists(false);
    }
  }, [examCode, checkExamCodeExists]);

  // Note: URL parameter loading removed as per new approach
  // Cloned exams are now loaded directly into the form after cloning

  // Real-time validation (debounced) - only after user interaction
  useEffect(() => {
    if (!hasInteracted || isSaveValidation) {
      return; // Don't override save validation errors
    }
    
    const timeoutId = setTimeout(() => {
      validateAll();
    }, 500); // Debounce validation to avoid too frequent updates

    return () => clearTimeout(timeoutId);
  }, [examTitle, examCode, examTimer, startDate, endDate, validateAll, hasInteracted, isSaveValidation]);

  // Initialize ShortUrlService with Firebase config
  useEffect(() => {
    const firebaseConfig = firebase.app().options;
    ShortUrlService.autoInit(firebaseConfig);
  }, []);

  // Add authentication listener to ensure user is authenticated
  useEffect(() => {
    const unsubscribe = firebase.auth().onAuthStateChanged((user) => {
      // User authentication state tracked silently
    });

    return () => unsubscribe();
  }, []);

  // Upload pending images and convert to short URLs
  const uploadPendingImages = async (sections) => {
    const updatedSections = JSON.parse(JSON.stringify(sections));
    let totalImages = 0;
    let uploadCount = 0;
    let totalOriginalSize = 0;
    let totalOptimizedSize = 0;
    let uploadErrors = [];

    // Count total pending images and calculate total size
    
    for (const section of updatedSections) {
      for (const question of section.questions) {
        const pendingFile = pendingImageFiles.get(question.id);
        
        if (pendingFile && !question.imageUrl.includes('firebase')) {
          totalImages++;
          totalOriginalSize += pendingFile.size;
        }
      }
    }

    if (totalImages === 0) {
      return { sections: updatedSections, uploadErrors: [] };
    }

    // Upload images
    for (let sectionIndex = 0; sectionIndex < updatedSections.length; sectionIndex++) {
      const section = updatedSections[sectionIndex];
      for (let questionIndex = 0; questionIndex < section.questions.length; questionIndex++) {
        const question = section.questions[questionIndex];
        
        // Check if there's a pending image file to upload
        const pendingFile = pendingImageFiles.get(question.id);
        if (pendingFile && !question.imageUrl.includes('firebase')) {
          try {
            // Validate the file object
            if (!(pendingFile instanceof File || pendingFile instanceof Blob)) {
              throw new Error(`Invalid file object: ${pendingFile.constructor.name}`);
            }
            

            
            // Upload image and get result
            const result = await ShortUrlService.uploadImageWithShortUrl(pendingFile, question.id);
            
            // Store the short URL in the database (saves storage space)
            updatedSections[sectionIndex].questions[questionIndex].imageUrl = result.shortUrl;
            
            uploadCount++;
            totalOptimizedSize += result.optimizedSize;
          } catch (error) {
            console.error(`❌ Failed to upload image for question ${question.id}:`, error);
            uploadErrors.push({
              questionId: question.id,
              questionText: question.text.substring(0, 50) + '...',
              error: error.message
            });
            // Keep the original file for retry
          }
        }
      }
    }

    if (uploadCount > 0) {
      // Clear the pending files after successful upload
      setPendingImageFiles(new Map());
    }

    // Check if all pending images were uploaded successfully
    if (totalImages > 0 && uploadCount === 0) {
      console.warn('No images were uploaded successfully out of', totalImages, 'pending images');
      uploadErrors.push({
        questionId: 'general',
        questionText: 'All pending images',
        error: 'Failed to upload any pending images. Please try again.'
      });
    }

    return { sections: updatedSections, uploadErrors };
  };

  // Legacy base64 image migration function (only for existing data)
  const migrateLegacyBase64Images = async (sections) => {
    const migratedSections = JSON.parse(JSON.stringify(sections));
    let totalImages = 0;
    let migratedCount = 0;
    let totalOriginalSize = 0;
    let totalOptimizedSize = 0;
    let migrationErrors = [];

    // Count legacy base64 images and calculate total size
    for (const section of migratedSections) {
      for (const question of section.questions) {
        if (question.imageUrl && question.imageUrl.startsWith('data:image')) {
          totalImages++;
          // Estimate base64 size (rough calculation)
          totalOriginalSize += question.imageUrl.length * 0.75; // Base64 is ~33% larger
        }
      }
    }

    // Only migrate if there are legacy base64 images
    if (totalImages === 0) {
      return { sections: migratedSections, migrationErrors: [] };
    }


    setMigrationProgress({ current: 0, total: totalImages });

    // Migrate legacy images
    for (let sectionIndex = 0; sectionIndex < migratedSections.length; sectionIndex++) {
      const section = migratedSections[sectionIndex];
      for (let questionIndex = 0; questionIndex < section.questions.length; questionIndex++) {
        const question = section.questions[questionIndex];
        
        if (question.imageUrl && question.imageUrl.startsWith('data:image')) {
          try {
            // Convert base64 to blob
            const response = await fetch(question.imageUrl);
            const blob = await response.blob();
            
            // Create a file object
            const file = new File([blob], `question_${question.id}.jpg`, { type: 'image/jpeg' });
            
            // Upload optimized image to Firebase Storage
            const result = await AdvancedImageOptimizer.uploadOptimizedImage(file, question.id);
            
            // Convert to short URL for database storage (saves space and prevents duplication)
            const shortUrl = await ShortUrlService.toShortUrl(result.url);
            
            // Update the question with the short URL
            migratedSections[sectionIndex].questions[questionIndex].imageUrl = shortUrl;
            migratedCount++;
            totalOptimizedSize += result.optimizedSize;
            

          } catch (error) {
            console.error(`❌ Failed to migrate legacy image for question ${question.id}:`, error);
            migrationErrors.push({
              questionId: question.id,
              questionText: question.text.substring(0, 50) + '...',
              error: error.message
            });
            // Keep the original base64 if migration fails
          }

          setMigrationProgress({ current: migratedCount, total: totalImages });
        }
      }
    }



    setMigrationProgress({ current: 0, total: 0 });
    return { sections: migratedSections, migrationErrors };
  };

  // Progress Modal Component
  const ProgressModal = () => {
    const handleModalClose = () => {
      // Only allow closing on success
      if (progressStatus === 'success') {
        setShowProgressModal(false);
        history.push('/admin-dashboard');
      }
    };

    const handleContinueButton = () => {
      if (progressStatus === 'success') {
        setShowProgressModal(false);
        history.push('/admin-dashboard');
      } else if (progressStatus === 'error') {
        setShowProgressModal(false);
        // Stay on edit page
      }
    };

    return (
      <Modal
        open={showProgressModal}
        onClose={(event, reason) => {
          // Only allow closing on success or if user clicks backdrop during error
          if (progressStatus === 'success' || (reason === 'backdropClick' && progressStatus === 'error')) {
            handleModalClose();
          }
        }}
        style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          padding: 20
        }}
        disableEscapeKeyDown={progressStatus !== 'success'}
      >
        <Box
          style={{
            backgroundColor: 'white',
            borderRadius: 16,
            padding: 32,
            maxWidth: 500,
            width: '100%',
            textAlign: 'center',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}
        >
          {/* Header */}
          <Box mb={3}>
            {progressStatus === 'uploading' && (
              <CloudUpload style={{ fontSize: 64, color: '#1976d2', marginBottom: 16 }} />
            )}
            {progressStatus === 'success' && (
              <CheckCircle style={{ fontSize: 64, color: '#4caf50', marginBottom: 16 }} />
            )}
            {progressStatus === 'error' && (
              <Error style={{ fontSize: 64, color: '#f44336', marginBottom: 16 }} />
            )}
            
            <Typography variant="h5" style={{ fontWeight: 600, marginBottom: 8 }}>
              {progressStatus === 'uploading' && 'Processing Assessment...'}
              {progressStatus === 'success' && 'Assessment Saved Successfully!'}
              {progressStatus === 'error' && 'Save Failed'}
            </Typography>
            
            <Typography variant="body2" color="textSecondary">
              {progressStatus === 'uploading' && 'Please wait while we process and save your assessment...'}
              {progressStatus === 'success' && 'Your assessment has been saved successfully.'}
              {progressStatus === 'error' && 'There was an error saving your assessment. Please try again.'}
            </Typography>
          </Box>

          {/* Progress Bar */}
          {progressStatus === 'uploading' && (
            <Box mb={3}>
              <LinearProgress 
                variant="indeterminate" 
                style={{ height: 8, borderRadius: 4 }}
              />
            </Box>
          )}

          {/* Action Button */}
          <Button
            variant="contained"
            color={progressStatus === 'success' ? 'primary' : progressStatus === 'error' ? 'secondary' : 'default'}
            size="large"
            onClick={handleContinueButton}
            disabled={progressStatus === 'uploading'}
            style={{
              borderRadius: 8,
              padding: '12px 32px',
              fontSize: 16,
              fontWeight: 600
            }}
          >
            {progressStatus === 'uploading' && (
              <>
                <CircularProgress size={20} style={{ marginRight: 8, color: 'white' }} />
                Processing...
              </>
            )}
            {progressStatus === 'success' && 'Go to Dashboard'}
            {progressStatus === 'error' && 'Continue Editing'}
          </Button>
        </Box>
      </Modal>
    );
  };

  // Save Exam Handler with comprehensive validation
  const handleSaveExam = useCallback(async () => {
    setIsSaving(true);
    
    // CRITICAL: Run STRICT validations to prevent saving with ANY errors
    const allValidationErrors = validateAllStrict();
    if (Object.keys(allValidationErrors).length > 0) {
      const errorCount = Object.keys(allValidationErrors).length;
      
      // CRITICAL: Set validation errors to state so they show up in UI
      setIsSaveValidation(true); // Prevent real-time validation from overriding
      setValidationErrors({...allValidationErrors});
      setHasInteracted(true); // Show validation errors in UI
      
      // Create a more detailed error message
      const errorMessages = Object.values(allValidationErrors).slice(0, 3);
      const errorMessage = errorMessages.length > 0 
        ? `Please fix ${errorCount} validation error${errorCount > 1 ? 's' : ''} before saving the assessment.\n\nFirst few errors:\n${errorMessages.map(err => `• ${err}`).join('\n')}`
        : `Please fix ${errorCount} validation error${errorCount > 1 ? 's' : ''} before saving the assessment.`;
      
      swal("Validation Error", errorMessage, "error");
      setIsSaving(false);
      return;
    }
    
    // Show progress modal and start upload process
    setShowProgressModal(true);
    setProgressStatus('uploading');
    
    // Upload pending images and migrate legacy base64 images
    let optimizedSections;
    let uploadErrors = [];
    let migrationErrors = [];
    
    try {
      // First, upload any pending images
      const uploadResult = await uploadPendingImages(sections);
      uploadErrors = uploadResult.uploadErrors || [];
      
      if (uploadErrors.length > 0) {
        console.warn('Some image uploads failed:', uploadErrors);
      }
      
      // Then, migrate any legacy base64 images
      const migrationResult = await migrateLegacyBase64Images(uploadResult.sections);
      migrationErrors = migrationResult.migrationErrors || [];
      
      if (migrationErrors.length > 0) {
        console.warn('Some legacy image migrations failed:', migrationErrors);
      }
      
      optimizedSections = migrationResult.sections.map(section => ({
      id: section.id,
      t: section.title.trim(),
      w: Number(section.weightage),
      q: section.questions.map(q => {
        const questionData = {
          id: q.id,
          type: q.type,
          text: q.text.trim(),
          ai: Boolean(q.enableAIEvaluation),
          maxScore: q.maxScore !== undefined ? Number(q.maxScore) : 1
        };
        
        // Save image URL if present (only Firebase Storage URLs or short URLs)
        if (q.imageUrl && q.imageUrl.trim()) {
          const imageUrl = q.imageUrl.trim();
          
          // Only save if it's a Firebase Storage URL or short URL, not a blob URL
          if (imageUrl.startsWith('https://firebasestorage.googleapis.com') || 
              imageUrl.startsWith('q-') || 
              imageUrl.startsWith('data:image')) {
            questionData.imageUrl = imageUrl;
          } else if (imageUrl.startsWith('blob:')) {
            // Don't save blob URLs - they're temporary and won't work
            console.warn(`Skipping blob URL for question ${q.id}: ${imageUrl}`);
            // Don't add imageUrl to questionData
          } else {
            // For any other URL format, save it (might be a valid external URL)
            questionData.imageUrl = imageUrl;
          }
        }
        if (q.problemDescription && q.problemDescription.trim()) {
          questionData.pd = q.problemDescription.trim();
        }
        if (q.instructions && q.instructions.trim()) {
          questionData.inst = q.instructions.trim();
        }
        if (q.boilerplateCode && q.boilerplateCode.trim()) {
          questionData.bc = q.boilerplateCode.trim();
        }
        if (q.evaluationKeywords && q.evaluationKeywords.trim()) {
          questionData.ek = q.evaluationKeywords.trim();
        }
        // Save evaluationPoints for text and video questions
        // Store exactly as user typed (string format) - no conversion
        if (q.type === 'text' || q.type === 'video') {
          // Always save evaluationPoints field (even if empty) for text and video questions
          if (q.evaluationPoints) {
            if (typeof q.evaluationPoints === 'string') {
              // Store as string exactly as user typed (trim only whitespace)
              questionData.evaluationPoints = q.evaluationPoints.trim();
            } else if (Array.isArray(q.evaluationPoints) && q.evaluationPoints.length > 0) {
              // If it's an array (from old data), convert to string
              questionData.evaluationPoints = q.evaluationPoints.join(', ');
            }
          } else {
            // Save empty string if field exists but is empty
            questionData.evaluationPoints = '';
          }
        }
        if (q.options && q.options.length > 0) {
          questionData.opts = q.options.map(opt => ({
            id: opt.id,
            t: opt.text.trim(),
            m: Number(opt.marks) || 0,
            c: Boolean(opt.isCorrect)
          }));
        }

        if (q.type === 'psyco' && q.psycoAnswer) {
          questionData.psycoAnswer = q.psycoAnswer.trim().toUpperCase();
        }
        // Save scenarioOptions for scenario-text questions
        if (q.type === 'scenario-text' && Array.isArray(q.scenarioOptions) && q.scenarioOptions.length > 0) {
          questionData.scenarioOptions = q.scenarioOptions.map(opt => ({
            title: opt.title.trim(),
            content: opt.content ? opt.content.trim() : '',
            evaluationPoints: Array.isArray(opt.evaluationPoints) ? opt.evaluationPoints.map(p => p.trim()) : []
          }));
        }
        return questionData;
      })
    }));
    } catch (error) {
      console.error('Error during image processing:', error);
      setProgressStatus('error');
      setIsSaving(false);
      return;
    }
    
    // Check if there were any errors and handle them
    const allErrors = [...uploadErrors, ...migrationErrors];
    if (allErrors.length > 0) {
      console.warn('Image processing errors detected:', allErrors);
      
      // Show error message to user
      const errorMessage = `Failed to process ${allErrors.length} image(s):\n\n${allErrors.map(err => `• Question: "${err.questionText}"\n  Error: ${err.error}`).join('\n\n')}`;
      
      swal("Image Processing Error", errorMessage, "error");
      setProgressStatus('error');
      setIsSaving(false);
      return;
    }
    
    // Additional check: Ensure no blob URLs are being saved
    let hasBlobUrls = false;
    const blobUrlQuestions = [];
    
    optimizedSections.forEach(section => {
      section.q.forEach(question => {
        if (question.imageUrl && question.imageUrl.startsWith('blob:')) {
          hasBlobUrls = true;
          blobUrlQuestions.push({
            questionId: question.id,
            questionText: question.text.substring(0, 50) + '...'
          });
        }
      });
    });
    
    if (hasBlobUrls) {
      console.error('Blob URLs detected in optimized sections:', blobUrlQuestions);
      const errorMessage = `Cannot save assessment with temporary image URLs. Please try uploading the images again.\n\nAffected questions:\n${blobUrlQuestions.map(q => `• "${q.questionText}"`).join('\n')}`;
      
      swal("Image Upload Error", errorMessage, "error");
      setProgressStatus('error');
      setIsSaving(false);
      return;
    }
    
    const startDateTime = parseISTInputToUTCTimestamp(startDate);
    const endDateTime = parseISTInputToUTCTimestamp(endDate);
    
    const examData = {
      title: examTitle.trim(),
      code: examCode.trim(),
      timer: parseInt(examTimer),
      start: startDateTime,
      end: endDateTime,
      sections: optimizedSections,
      created: firebase.database.ServerValue.TIMESTAMP,
      dynamic: true,
      by: sessionStorage.getItem("email") || 'Unknown',
      cheatDetectionSettings: cheatDetectionSettings
    };
    
    // CRITICAL: Final safety check before saving to prevent database corruption
    if (!examData.code || examData.code.length < 3) {
      swal("Critical Error", "Invalid assessment code. Cannot save to prevent database corruption.", "error");
      return;
    }
    
    // CRITICAL: Check if exam code already exists before saving
    if (examCodeExists) {
      swal("Duplicate Assessment Code", "This assessment code already exists. Please choose a different code.", "error");
      return;
    }
    

    
    // Save to Firebase
    try {
      await firebase.database().ref(`TestPapers/${examData.code}`).set(examData);
      
      // Invalidate related caches after successful save
      ExamCacheService.clearExamCache(examData.code);
      ExamCacheService.clearExamListCache();
      console.log(`🗑️ Cache invalidated for exam: ${examData.code}`);
      
      // Update progress status to success
      setProgressStatus('success');
      
    } catch (err) {
      console.error('❌ Failed to save assessment:', err);
      setProgressStatus('error');
    } finally {
      setIsSaving(false);
    }
  }, [validateAllStrict, sections, history, examTitle, examCode, examTimer, startDate, endDate, cheatDetectionSettings, examCodeExists, pendingImageFiles]);



  // Clone Exam Functions - New Approach with Smart Caching




  const handleCloneExam = useCallback(async () => {
    if (!selectedExam) {
      swal("No Assessment Selected", "Please select an assessment to clone.", "warning");
      return;
    }

    // Show confirmation dialog with exam details
    const result = await swal({
      title: "Confirm Clone",
      text: `Are you sure you want to clone this assessment?\n\nTitle: ${selectedExam.title}\nCode: ${selectedExam.code}\nSections: ${selectedExam.sectionsCount || selectedExam.sections?.length || 0}\nTimer: ${selectedExam.timer} minutes\n\nNote: The assessment will be loaded into the form for editing. You can review and modify all questions before saving.`,
      icon: "warning",
      buttons: ["Cancel", "Clone Exam"],
      dangerMode: true,
    });

    if (!result) {
      return; // User cancelled
    }

    setIsCloning(true);
    try {
      // Use full exam data if already loaded by enhanced search
      let fullExamData = selectedExam.fullData;
      
      if (!fullExamData) {
        console.log('🌐 Fetching exam details from Firebase...');
        // Load the full exam data from Firebase as fallback
        const examRef = firebase.database().ref(`TestPapers/${selectedExam.code}`);
        const snapshot = await examRef.once('value');
        fullExamData = snapshot.val();
        
        if (fullExamData) {
          // Cache the exam details for future use
          ExamCacheService.setExamCache(selectedExam.code, fullExamData);
          console.log(`💾 Exam details cached: ${selectedExam.code}`);
        }
      } else {
        console.log(`📖 Using pre-loaded exam details: ${selectedExam.code}`);
      }

      if (!fullExamData) {
        throw new Error('Assessment data not found');
      }


      // Prepare cloned exam data with "Copy" appended (no database save)
      const clonedExamData = {
        title: `${selectedExam.title} (Copy)`,
        code: `${selectedExam.code}_copy_${Date.now()}`, // Generate unique code
        timer: selectedExam.timer || 60,
        start: null, // Reset dates for new exam
        end: null,
        sections: fullExamData.sections || [],
        dynamic: true,
        clonedFrom: selectedExam.code,
        originalTitle: selectedExam.title,
        cheatDetectionSettings: fullExamData.cheatDetectionSettings || null // Include cheat detection settings
      };

      swal("Success", `Assessment "${selectedExam.title}" has been loaded for editing. Review all questions and click "Save Assessment Template" when ready.`, "success");
      
      // Reset selection
      setSelectedExam(null);
      
      // Load the cloned exam data into the form for editing
      loadClonedExamData(clonedExamData);
      
    } catch (error) {
      console.error('Error cloning assessment:', error);
      swal("Clone Error", "Unable to retrieve assessment data. Please try again.", "error");
    } finally {
      setIsCloning(false);
    }
  }, [selectedExam]);

  const loadClonedExamData = useCallback(async (examData) => {
    // Set exam metadata
    setExamTitle(examData.title || '');
    setExamCode(examData.code || '');
    setExamTimer(examData.timer || 60);
    setStartDate('');
    setEndDate('');
    
    // Set cheat detection settings if they exist, otherwise use defaults
    if (examData.cheatDetectionSettings && Object.keys(examData.cheatDetectionSettings).length > 0) {
      // Merge with defaults to ensure all settings are present
      const mergedSettings = {
        enableTabSwitchDetection: true,
        enableCopyPasteDetection: true,
        enableFullscreenDetection: true,
        enableWebcamDetection: true,
        enableKeyboardDetection: true,
        enableContextMenuDetection: true,
        enableSidePanelDetection: true,
        enableFocusLossDetection: true,
        ...examData.cheatDetectionSettings
      };
      
      setCheatDetectionSettings(mergedSettings);
    } else {
      // Use default settings for cloned exams
      const defaultSettings = {
        enableTabSwitchDetection: true,
        enableCopyPasteDetection: true,
        enableFullscreenDetection: true,
        enableWebcamDetection: true,
        enableKeyboardDetection: true,
        enableContextMenuDetection: true,
        enableSidePanelDetection: true,
        enableFocusLossDetection: true
      };
      setCheatDetectionSettings(defaultSettings);
    }
    
    // Set sections if they exist
    if (examData.sections && Array.isArray(examData.sections)) {
      const processedSections = await Promise.all(examData.sections.map(async (section) => {
        const processedQuestions = await Promise.all((section.q || section.questions || []).map(async (question) => {
          // Generate a new unique ID for the cloned question
          const newQuestionId = `q-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`;
          
          // Handle image cloning - create new image files for each cloned question
          let newImageUrl = question.imageUrl || '';
          let clonedImageFile = null;
          
          if (newImageUrl) {
            try {
              // Get the full URL from the short URL
              let fullImageUrl;
              if (newImageUrl.startsWith('q-') || newImageUrl.startsWith('img_')) {
                fullImageUrl = await ShortUrlService.toFullUrl(newImageUrl);
              } else if (newImageUrl.startsWith('https://firebasestorage.googleapis.com')) {
                fullImageUrl = newImageUrl;
              }
              
              if (fullImageUrl) {
                // Extract the path from the Firebase Storage URL
                const url = new URL(fullImageUrl);
                const pathMatch = url.pathname.match(/\/o\/(.+?)\?/);
                
                if (pathMatch) {
                  const imagePath = decodeURIComponent(pathMatch[1]);
                  
                  // Use Firebase Storage SDK to download the image
                  const storageRef = firebase.storage().ref(imagePath);
                  const blob = await storageRef.getBlob();
                  
                  // Create a new file object with a unique name
                  const fileExtension = blob.type.split('/')[1] || 'jpg';
                  const newFileName = `cloned_${newQuestionId}_${Date.now()}.${fileExtension}`;
                  clonedImageFile = new File([blob], newFileName, { type: blob.type });
                  
                  // Create a temporary blob URL for preview
                  newImageUrl = URL.createObjectURL(clonedImageFile);
                }
              }
            } catch (error) {
              console.warn(`Failed to clone image for question ${question.id}:`, error);
              newImageUrl = ''; // Clear image if cloning fails
            }
          }
          
          return {
            id: newQuestionId,
            type: question.type || 'mcq-single',
            text: question.text || '',
            maxScore: question.maxScore || 1,
            problemDescription: question.pd || question.problemDescription || '',
            instructions: question.inst || question.instructions || '',
            boilerplateCode: question.bc || question.boilerplateCode || '',
            evaluationKeywords: question.ek || question.evaluationKeywords || '',
            evaluationPoints: (() => {
                // Combine evaluationPoints and evaluationKeywords for backward compatibility
                const evalPoints = question.evaluationPoints || '';
                const evalKeywords = question.ek || question.evaluationKeywords || '';
                if (evalPoints && evalKeywords) {
                    // Both exist - combine them (remove duplicates)
                    const pointsArray = typeof evalPoints === 'string' 
                        ? evalPoints.split(',').map(p => p.trim()).filter(Boolean)
                        : Array.isArray(evalPoints) ? evalPoints : [];
                    const keywordsArray = typeof evalKeywords === 'string'
                        ? evalKeywords.split(',').map(k => k.trim()).filter(Boolean)
                        : Array.isArray(evalKeywords) ? evalKeywords : [];
                    const combined = [...pointsArray, ...keywordsArray];
                    return [...new Set(combined)].join(', ');
                }
                return evalPoints || evalKeywords || ((question.type === 'text' || question.type === 'video') ? '' : undefined);
            })(),
            imageUrl: newImageUrl,
            enableAIEvaluation: question.ai || question.enableAIEvaluation || false,
            options: (question.opts || question.options || []).map(option => ({
              id: `o-${Date.now()}-${Math.random().toString(36).substring(2, 8)}`,
              text: option.t || option.text || '',
              marks: option.m || option.marks || 0,
              isCorrect: option.c || option.isCorrect || false
            })),
            scenarioOptions: question.scenarioOptions || [],
            psycoAnswer: question.psycoAnswer || '',
            clonedImageFile: clonedImageFile
          };
        }));

        return {
          id: section.id || `s-${Date.now()}-${Math.random()}`,
          title: section.t || section.title || '',
          weightage: section.w || section.weightage || 0,
          questions: processedQuestions
        };
      }));

      // Store cloned image files for later upload during save
      const clonedImageFiles = new Map();
      const cleanedSections = processedSections.map(section => ({
        ...section,
        questions: section.questions.map(question => {
          // Store the cloned image file for later upload
          if (question.clonedImageFile) {
            clonedImageFiles.set(question.id, question.clonedImageFile);
          }
          
          // Remove the clonedImageFile property from the question object
          const { clonedImageFile, ...cleanQuestion } = question;
          return cleanQuestion;
        })
      }));
      
      if (clonedImageFiles.size > 0) {
        setPendingImageFiles(clonedImageFiles);
      }
      
      // Set the cleaned sections (without clonedImageFile property)
      setSections(cleanedSections);
    }
    
    swal("Assessment Loaded", `Cloned assessment "${examData.title}" has been loaded successfully. You can now edit and save it.`, "success");
  }, []);

  // Enhanced search handlers
  const handleEnhancedSelectExam = useCallback((exam) => {
    setSelectedExam({
      ...exam,
      // Ensure compatibility with existing structure
      sections: exam.fullData?.sections || exam.sections || []
    });
    
    // Log full data status for debugging
    if (exam.fullData) {
      console.log(`✅ Full exam data available for ${exam.code}:`, {
        sections: exam.fullData.sections?.length || 0,
        hasQuestions: exam.fullData.sections?.some(s => (s.q || s.questions)?.length > 0) || false
      });
    } else if (exam.isLoading) {
      console.log(`⏳ Loading full data for ${exam.code}...`);
    } else if (exam.error) {
      console.log(`❌ Error loading full data for ${exam.code}:`, exam.error);
    }
  }, []);

  const handleResetCloneSection = useCallback(() => {
    setSelectedExam(null);
  }, []);

  // Prepare exam object for preview
  const examForPreview = {
    title: examTitle,
    code: examCode,
    timer: examTimer,
    sections: sections.map(s => ({ ...s }))
  };

  return (
    <div style={{ padding: 24, maxWidth: 1200, margin: '0 auto' }}>
      <TopMenu showHome showLogout />
      
      {/* Clone Exam Section - Enhanced */}
      <Paper elevation={2} style={{ 
        padding: 20, 
        marginBottom: 24, 
        backgroundColor: '#ffffff', 
        maxWidth: 700, 
        margin: '80px auto 24px auto', 
        borderRadius: 12,
        border: '1px solid #e3f2fd'
      }}>
        {/* Header */}
        <Box display="flex" alignItems="center" justifyContent="center" mb={3}>
          <Box display="flex" alignItems="center">
            <FileCopy style={{ marginRight: 8, fontSize: 20, color: '#1976d2' }} />
            <Typography variant="h6" style={{ fontWeight: 600, color: '#1976d2', fontSize: 18 }}>
              Clone Existing Assessment
            </Typography>
          </Box>
        </Box>

        <Typography variant="body2" style={{ 
          color: '#666', 
          fontSize: 13, 
          textAlign: 'center', 
          marginBottom: 20,
          lineHeight: 1.5 
        }}>
          Search and select an existing assessment to use as a template. You can modify all questions before saving.
        </Typography>

        {/* Enhanced Search Component */}
        <EnhancedExamSearch
          onSelectExam={handleEnhancedSelectExam}
          selectedExam={selectedExam}
          onReset={handleResetCloneSection}
          isLoading={isCloning}
        />

        {/* Clone Button */}
        {selectedExam && (
          <Box display="flex" justifyContent="center" mt={3}>
            <Button
              variant="contained"
              color="primary"
              size="medium"
              onClick={handleCloneExam}
              disabled={isCloning || selectedExam.isLoading || !selectedExam.fullData}
              startIcon={
                (isCloning || selectedExam.isLoading) ? 
                <CircularProgress size={18} /> : 
                <FileCopy style={{ fontSize: 16 }} />
              }
              style={{ 
                padding: '8px 24px', 
                fontSize: 14,
                fontWeight: 600,
                borderRadius: 8,
                textTransform: 'none',
                boxShadow: selectedExam.fullData && !selectedExam.isLoading ? 
                  '0 4px 12px rgba(25, 118, 210, 0.3)' : 
                  '0 2px 6px rgba(0, 0, 0, 0.1)'
              }}
            >
              {isCloning ? 'Cloning Assessment...' : 
               selectedExam.isLoading ? 'Loading Full Data...' :
               selectedExam.error ? 'Error Loading Data' :
               !selectedExam.fullData ? 'Waiting for Data...' :
               'Load Assessment for Editing'}
            </Button>
          </Box>
        )}
      </Paper>

      <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', marginTop: 24, marginBottom: 24 }}>
        <Typography variant="h5" style={{ fontWeight: 700, color: '#004085', fontSize: 32, textAlign: 'center', letterSpacing: 1 }}>
          Assessment Template Builder
        </Typography>
      </div>
      
      {/* Validation Summary - Show whenever there are validation errors */}
      {Object.keys(validationErrors).length > 0 && (
        <Box mb={2} p={2} border={1} borderColor="#f44336" borderRadius={4} bgcolor="#ffebee">
          <Typography variant="h6" style={{ color: '#d32f2f', fontWeight: 600, marginBottom: 8 }}>
            ⚠️ Validation Errors ({Object.keys(validationErrors).length})
          </Typography>
          <Typography variant="body2" style={{ color: '#d32f2f' }}>
            Please fix the following errors before saving the assessment:
          </Typography>
          <ul style={{ margin: '8px 0', paddingLeft: 20 }}>
            {Object.values(validationErrors).slice(0, 5).map((error, index) => (
              <li key={index} style={{ color: '#d32f2f', marginBottom: 4 }}>
                {error}
              </li>
            ))}
            {Object.keys(validationErrors).length > 5 && (
              <li style={{ color: '#d32f2f', fontStyle: 'italic' }}>
                ... and {Object.keys(validationErrors).length - 5} more errors
              </li>
            )}
          </ul>
        </Box>
      )}
      <ExamMetaForm
        examTitle={examTitle}
        setExamTitle={(value) => {
          setIsSaveValidation(false); // Reset save validation flag when user makes changes
          setExamTitle(value);
        }}
        examCode={examCode}
        setExamCode={(value) => {
          setIsSaveValidation(false); // Reset save validation flag when user makes changes
          setExamCode(value);
        }}
        examTimer={examTimer}
        setExamTimer={(value) => {
          setIsSaveValidation(false); // Reset save validation flag when user makes changes
          setExamTimer(value);
        }}
        startDate={startDate}
        setStartDate={(value) => {
          setIsSaveValidation(false); // Reset save validation flag when user makes changes
          setStartDate(value);
        }}
        endDate={endDate}
        setEndDate={(value) => {
          setIsSaveValidation(false); // Reset save validation flag when user makes changes
          setEndDate(value);
        }}
        validationErrors={validationErrors}
        hasInteracted={hasInteracted}
        isCheckingDuplicate={isCheckingDuplicate}
        examCodeExists={examCodeExists}
        cheatDetectionSettings={cheatDetectionSettings}
        setCheatDetectionSettings={setCheatDetectionSettings}
      />
      
      {/* Total Weightage Display - Show whenever there are errors or after interaction */}
      {(hasInteracted || validationErrors.totalWeightage) && (
        <Box mt={2} mb={2} p={2} border={1} borderRadius={4} borderColor={Math.abs(totalWeightage - 100) > 0.1 ? '#f44336' : '#4caf50'} bgcolor={Math.abs(totalWeightage - 100) > 0.1 ? '#ffebee' : '#e8f5e8'}>
          <Typography variant="h6" style={{ color: Math.abs(totalWeightage - 100) > 0.1 ? '#d32f2f' : '#2e7d32', fontWeight: 600 }}>
            Total Section Weightage: {totalWeightage}% {Math.abs(totalWeightage - 100) > 0.1 ? '(Must equal 100%)' : '(✓ Valid)'}
          </Typography>
          {validationErrors.totalWeightage && (
            <Typography variant="body2" style={{ color: '#d32f2f', marginTop: 4 }}>
              {validationErrors.totalWeightage}
            </Typography>
          )}
        </Box>
      )}
      {sections.map((section, idx) => (
        <SectionEditor
          key={section.id}
          section={section}
          sectionIndex={idx}
          onSectionChange={handleSectionChange}
          onRemoveSection={removeSection}
          onQuestionChange={handleQuestionChange}
          onAddQuestion={addQuestion}
          onRemoveQuestion={removeQuestion}
          validationErrors={validationErrors}
          hasInteracted={hasInteracted}
        />
      ))}
      <Box mt={2}>
        <Button variant="contained" color="primary" onClick={addSection} style={{ marginRight: 16 }}>
          Add Assessment Section
        </Button>

        <Button 
          variant="contained" 
          color="secondary" 
          onClick={handleSaveExam} 
          disabled={isSaving}
          style={{ marginRight: 16 }}
        >
          {isSaving ? (
            <>
              <CircularProgress size={20} style={{ marginRight: 8, color: 'white' }} />
              Saving...
            </>
          ) : (
            'Save Assessment Template'
          )}
        </Button>
        <Button variant="outlined" onClick={() => setShowPreview(p => !p)}>
          {showPreview ? 'Hide Preview' : 'Preview Assessment'}
        </Button>
      </Box>
            <ExamPreview exam={examForPreview} open={showPreview} onClose={() => setShowPreview(false)} />
      
      {/* Progress Modal */}
      <ProgressModal />
    </div>
  );
};

export default DynamicExamCreator; 